import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import { fetchConversationsEmployees } from '@/entities/conversations/model/api/api';
import { createEmployee, deleteEmployee, mergeEmployee, updateEmployee } from './api';
import type { EmployeeCreateRequest, EmployeeUpdateRequest } from '../types';

/**
 * Query keys для сотрудников
 */
export const employeeKeys = {
  all: [API_TAGS.EMPLOYEES] as const,
  list: (searchQuery?: string) => [...employeeKeys.all, 'list', searchQuery] as const,
};

const LIMIT = 50;

/**
 * Хук для получения списка сотрудников с бесконечной пагинацией
 */
export const useEmployeesInfinite = (searchQuery?: string) => {
  return useInfiniteQuery({
    queryKey: employeeKeys.list(searchQuery),
    queryFn: ({ pageParam }) =>
      fetchConversationsEmployees({
        limit: LIMIT,
        offset: pageParam * LIMIT,
        'employee-name': searchQuery || undefined,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.employees.length < LIMIT) return undefined;
      return lastPage.total <= LIMIT * (lastPageParam + 1) ? undefined : lastPageParam + 1;
    },
  });
};

/**
 * Хук для создания сотрудника
 */
export const useCreateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EmployeeCreateRequest) => createEmployee(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
};

/**
 * Хук для обновления сотрудника
 */
export const useUpdateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: EmployeeUpdateRequest }) => updateEmployee(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
};

/**
 * Хук для удаления сотрудника
 */
export const useDeleteEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, targetEmployeeId }: { id: number; targetEmployeeId: number }) =>
      deleteEmployee(id, targetEmployeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
};

/**
 * Хук для объединения дубликатов сотрудника
 */
export const useMergeEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => mergeEmployee(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
};
